import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import Progress from "@/components/Progress";
import SegmentedButton from "@/components/SegmentedButton";
import usePageTitle from "@/hooks/usePageTitle";
import { useParams } from "@/hooks/useSearchParams";
import { useErrorBanner } from "@/providers/ErrorBannerProvider/useErrorBanner";
import TabLayout from "../_TabLayout";
import { useConfigurationAccess } from "../access";
import { shouldSwitchConfigurationToLocalBackend } from "../localBackend";
import ConfigurationLoadError from "../_components/ConfigurationLoadError";
import TopicsEditor, { topicFocusKey } from "./_components/TopicsEditor";
import {
  INTERACTION_TOPIC_CONFIGS_QUERY,
  INTERACTION_TOPIC_DEPARTMENT_PARAM,
  INTERACTION_TOPIC_DEPARTMENTS,
  OTHER_TOPIC,
  parseInteractionTopicDepartment,
  type InteractionTopicConfig,
  type InteractionTopicInput
} from "./queries";
import { UPDATE_INTERACTION_TOPIC_CONFIG_MUTATION, type UpdateInteractionTopicConfigVariables } from "./mutations";
import { hasValidationErrors, isTopicLocked, topicsEqual, validateInteractionTopics } from "./_utils/topicsValidation";

const AUTOSAVE_DEBOUNCE_MS = 700;

type SaveStatus = "clean" | "saving" | "saved" | "error" | "invalid";

function insertBeforeOther<T>(items: T[], item: T, isOther: (value: T) => boolean): T[] {
  const otherIndex = items.findIndex(isOther);
  if (otherIndex === -1) return [...items, item];
  return [...items.slice(0, otherIndex), item, ...items.slice(otherIndex)];
}

function insertionIndexBeforeOther<T>(items: T[], isOther: (value: T) => boolean): number {
  const otherIndex = items.findIndex(isOther);
  return otherIndex === -1 ? items.length : otherIndex;
}

function isOtherName(name: string): boolean {
  return name.trim() === OTHER_TOPIC;
}

// Server lists carry the canonical trailing Other; editing works on the raw
// lines without it (the backend re-appends it on save).
function stripCanonicalOther(subtopics: string[]): string[] {
  const lines = [...subtopics];
  if (lines.length > 0 && isOtherName(lines[lines.length - 1])) lines.pop();
  return lines;
}

function toEditableTopic(topic: InteractionTopicConfig["topics"][number]): InteractionTopicInput {
  return {
    name: topic.name,
    subtopics: stripCanonicalOther(topic.subtopics),
    code: topic.code ?? null,
    defaultPrivate: topic.defaultPrivate ?? null
  };
}

function toSavePayload(topic: InteractionTopicInput): InteractionTopicInput {
  return {
    name: topic.name,
    subtopics: topic.subtopics.map((line) => line.trim()).filter((line) => line.length > 0),
    code: topic.code ?? null,
    defaultPrivate: topic.defaultPrivate ?? null
  };
}

export default function InteractionTopicsTabPage() {
  usePageTitle("/configuration/interaction-topics", "Member 360 Configuration | Interaction Topics");
  const { loaded, canView } = useConfigurationAccess();
  const { params, addSearchParam } = useParams();
  const { setError, clearError } = useErrorBanner();
  const department = parseInteractionTopicDepartment(params[INTERACTION_TOPIC_DEPARTMENT_PARAM]);

  const [topicsByDept, setTopicsByDept] = useState<Record<string, InteractionTopicInput[]> | null>(null);
  const [lockVersions, setLockVersions] = useState<Record<string, number>>({});
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("clean");
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const savedSnapshot = useRef<Record<string, InteractionTopicInput[]>>({});
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveEpoch = useRef(0);
  // Serializes saves so a follow-up save always carries the lock version from
  // the previous save's echo instead of racing it into a false conflict.
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  const latestTopics = useRef<Record<string, InteractionTopicInput[]> | null>(null);
  const latestDepartment = useRef(department);
  const latestLockVersions = useRef<Record<string, number>>({});
  const conflictedDept = useRef<string | null>(null);

  const skip = !loaded || !canView || shouldSwitchConfigurationToLocalBackend();
  const { data, loading, error, refetch } = useQuery(INTERACTION_TOPIC_CONFIGS_QUERY, {
    skip,
    fetchPolicy: "network-only"
  });

  const [updateTopics, { loading: saving }] = useMutation(UPDATE_INTERACTION_TOPIC_CONFIG_MUTATION, {
    refetchQueries: [{ query: INTERACTION_TOPIC_CONFIGS_QUERY }]
  });
  const saveTopicsRef = useRef<(dept: string, topics: InteractionTopicInput[]) => Promise<unknown>>(() =>
    Promise.resolve()
  );
  latestTopics.current = topicsByDept;
  latestDepartment.current = department;
  latestLockVersions.current = lockVersions;

  useEffect(() => {
    if (data && !topicsByDept) {
      const initial: Record<string, InteractionTopicInput[]> = {};
      const versions: Record<string, number> = {};
      for (const config of data.interactionTopicConfigs) {
        initial[config.department] = config.topics.map(toEditableTopic);
        versions[config.department] = config.lockVersion ?? 0;
      }
      savedSnapshot.current = initial;
      setTopicsByDept(initial);
      setLockVersions(versions);
    }
  }, [data, topicsByDept]);

  // After a version conflict, the refetch lands here: replace the conflicted
  // department's local state with the server list (the conflicting local edits
  // are discarded — the banner says so).
  useEffect(() => {
    const dept = conflictedDept.current;
    if (!dept || !data || !topicsByDept) return;
    const config = data.interactionTopicConfigs.find((candidate) => candidate.department === dept);
    if (!config) return;
    conflictedDept.current = null;
    const serverTopics = config.topics.map(toEditableTopic);
    savedSnapshot.current[dept] = serverTopics;
    setTopicsByDept((prev) => (prev ? { ...prev, [dept]: serverTopics } : prev));
    setLockVersions((prev) => ({ ...prev, [dept]: config.lockVersion ?? 0 }));
    setSaveStatus("clean");
  }, [data, topicsByDept]);

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      // Flush a pending valid save on unmount so navigating away mid-debounce
      // does not drop edits. Goes through the chained save path (waiting for
      // any in-flight echo's lock version); post-unmount setState is a no-op.
      const dept = latestDepartment.current;
      const pending = latestTopics.current?.[dept];
      const saved = savedSnapshot.current[dept];
      if (
        pending &&
        saved &&
        !topicsEqual(pending, saved) &&
        !hasValidationErrors(validateInteractionTopics(pending))
      ) {
        void saveTopicsRef.current(dept, pending);
      }
    };
  }, []);

  const currentTopics = useMemo(() => topicsByDept?.[department] ?? [], [topicsByDept, department]);
  const errors = useMemo(() => validateInteractionTopics(currentTopics), [currentTopics]);
  const isDirty = useMemo(() => {
    const saved = savedSnapshot.current[department];
    if (!topicsByDept || !saved) return false;
    return !topicsEqual(currentTopics, saved);
  }, [topicsByDept, currentTopics, department]);

  const saveTopicsInner = useCallback(
    async (dept: string, topics: InteractionTopicInput[]) => {
      const epochAtSend = saveEpoch.current;
      setSaveStatus("saving");
      clearError();
      try {
        const variables: UpdateInteractionTopicConfigVariables = {
          input: {
            department: dept,
            topics: topics.map(toSavePayload),
            lockVersion: latestLockVersions.current[dept] ?? 0
          }
        };
        const result = await updateTopics({ variables });
        const payload = result.data?.updateInteractionTopicConfig;
        if (payload?.success && payload.config) {
          const savedTopics = payload.config.topics.map(toEditableTopic);
          savedSnapshot.current[dept] = savedTopics;
          // Sync the ref immediately: a chained follow-up save reads it in the
          // same tick, before React re-renders with the new state.
          const nextVersion = payload.config?.lockVersion ?? 0;
          latestLockVersions.current = { ...latestLockVersions.current, [dept]: nextVersion };
          setLockVersions((prev) => ({ ...prev, [dept]: nextVersion }));
          // Only sync the server list into edit state when nothing changed
          // mid-flight; otherwise keep the newer local edits (still dirty, so
          // the autosave effect will send them next).
          if (saveEpoch.current === epochAtSend) {
            setTopicsByDept((prev) => (prev ? { ...prev, [dept]: savedTopics } : prev));
          }
          setSaveStatus("saved");
        } else if (payload && !payload.success && /another admin saved/i.test(payload.message ?? "")) {
          // Version conflict: another admin saved this department first.
          // Reload the server list; the conflicting local edits are discarded
          // (the banner says so) so the next save carries a fresh version.
          conflictedDept.current = dept;
          const deptLabel = INTERACTION_TOPIC_DEPARTMENTS.find((candidate) => candidate.value === dept)?.label ?? dept;
          setSaveStatus("error");
          setError(
            `${payload.message ?? "Couldn't save interaction topics."} Loading the latest ${deptLabel} topics — conflicting edits were discarded.`
          );
          void refetch();
        } else {
          setSaveStatus("error");
          setError(payload?.message || "Couldn't save interaction topics.");
        }
      } catch (caught) {
        setSaveStatus("error");
        setError(caught instanceof Error ? caught.message : "Couldn't save interaction topics.");
      }
    },
    [updateTopics, setError, clearError, refetch]
  );

  // Queues saves behind each other so a follow-up save waits for the previous
  // echo (and its lock version) instead of racing it into a false conflict.
  // A rejection never breaks the chain for later saves.
  const saveTopics = useCallback(
    (dept: string, topics: InteractionTopicInput[]) => {
      const run = saveChainRef.current.then(() => saveTopicsInner(dept, topics));
      saveChainRef.current = run.catch(() => {});
      return run;
    },
    [saveTopicsInner]
  );
  saveTopicsRef.current = saveTopics;

  useEffect(() => {
    if (!topicsByDept || !savedSnapshot.current[department]) return;
    if (!isDirty) {
      setSaveStatus((status) => (status === "saving" ? status : "clean"));
      return;
    }
    if (hasValidationErrors(errors)) {
      setSaveStatus("invalid");
      return;
    }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void saveTopics(department, currentTopics);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [topicsByDept, department, currentTopics, isDirty, errors, saveTopics]);

  const handleDeptChange = useCallback(
    (value: string | null) => {
      if (!value || value === department) return;
      const current = topicsByDept?.[department];
      const saved = savedSnapshot.current[department];
      if (
        current &&
        saved &&
        !topicsEqual(current, saved) &&
        !hasValidationErrors(validateInteractionTopics(current))
      ) {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        void saveTopics(department, current);
      }
      addSearchParam(INTERACTION_TOPIC_DEPARTMENT_PARAM, value);
    },
    [department, topicsByDept, saveTopics, addSearchParam]
  );

  const updateDeptTopics = useCallback(
    (dept: string, updater: (topics: InteractionTopicInput[]) => InteractionTopicInput[]) => {
      saveEpoch.current += 1;
      setTopicsByDept((prev) => {
        if (!prev) return prev;
        return { ...prev, [dept]: updater(prev[dept] ?? []) };
      });
    },
    []
  );

  const handleChangeTopicName = useCallback(
    (topicIndex: number, name: string) => {
      updateDeptTopics(department, (topics) => {
        if (isTopicLocked(topics, topicIndex)) return topics;
        return topics.map((topic, index) => (index === topicIndex ? { ...topic, name } : topic));
      });
    },
    [department, updateDeptTopics]
  );

  const handleChangeSubtopics = useCallback(
    (topicIndex: number, text: string) => {
      // Store the raw textarea lines untouched — trimming and the canonical
      // Other happen at save serialization, never while typing.
      const lines = text.split("\n");
      updateDeptTopics(department, (topics) =>
        topics.map((topic, index) => (index === topicIndex ? { ...topic, subtopics: lines } : topic))
      );
    },
    [department, updateDeptTopics]
  );

  const handleAddTopic = useCallback(() => {
    const topics = topicsByDept?.[department] ?? [];
    const insertAt = insertionIndexBeforeOther(topics, (topic) => isOtherName(topic.name));
    updateDeptTopics(department, (current) =>
      insertBeforeOther(current, { name: "", subtopics: [], code: null, defaultPrivate: null }, (topic) =>
        isOtherName(topic.name)
      )
    );
    setFocusKey(topicFocusKey(insertAt));
  }, [department, topicsByDept, updateDeptTopics]);

  const handleRemoveTopic = useCallback(
    (topicIndex: number) => {
      updateDeptTopics(department, (topics) => {
        if (isTopicLocked(topics, topicIndex)) return topics;
        return topics.filter((_, index) => index !== topicIndex);
      });
    },
    [department, updateDeptTopics]
  );

  const handleFocusApplied = useCallback(() => {
    setFocusKey(null);
  }, []);

  const saveStatusText = useMemo(() => {
    if (saving || saveStatus === "saving") return "Saving...";
    if (saveStatus === "error") return "Couldn't save — edit to retry.";
    if (saveStatus === "invalid") return "Fix errors above to save.";
    // Dirty state is the source of truth: after a mid-flight skip the server
    // echo is stale, so "saved" must not over-claim while edits remain.
    if (!isDirty) return "All changes saved.";
    return "Unsaved changes.";
  }, [saving, saveStatus, isDirty]);

  return (
    <TabLayout>
      <div className="mt-6">
        <SegmentedButton
          ariaLabel="Department"
          options={INTERACTION_TOPIC_DEPARTMENTS.map((dept) => ({
            label: dept.label,
            value: dept.value
          }))}
          value={department}
          onChange={handleDeptChange}
          fullWidth
        />
      </div>
      <div className="pt-12" />
      {error ? (
        <ConfigurationLoadError onRetry={() => refetch()} />
      ) : loading || !topicsByDept ? (
        <div className="flex items-center gap-3" data-testid="interaction-topics-loading">
          <Progress size={20} />
          <span className="text-body-medium text-sys-brand-on-surface-variant">Loading topics...</span>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between gap-4">
            <p className="text-body-medium text-sys-brand-on-surface-variant">
              Changes save automatically and appear in interaction dropdowns.
            </p>
            <p
              className="shrink-0 text-body-small text-sys-brand-on-surface-variant"
              aria-live="polite"
              data-testid="interaction-topics-save-status"
            >
              {saveStatusText}
            </p>
          </div>
          <TopicsEditor
            topics={currentTopics}
            errors={errors}
            focusKey={focusKey}
            onFocusApplied={handleFocusApplied}
            onChangeTopicName={handleChangeTopicName}
            onChangeSubtopics={handleChangeSubtopics}
            onAddTopic={handleAddTopic}
            onRemoveTopic={handleRemoveTopic}
          />
        </div>
      )}
    </TabLayout>
  );
}
